import { Switch as BaseSwitch, ProgressBar, type SwitchProps } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { css, cx } from "../../../../styled-system/css";
import { ImSpinner2 } from "react-icons/im";

interface Props extends Omit<SwitchProps, "children"> {
  children?: React.ReactNode;
  className?: string;
  isPending?: boolean;
}

export function Switch({ children, isPending, ...props }: Props) {
  const { t } = useTranslation(["common"]);
  return (
    <BaseSwitch
      {...props}
      isDisabled={props.isDisabled || isPending}
      className={cx(
        css({
          boxSizing: "border-box",
          cursor: "pointer",
          display: "flex",
          position: "relative",
          gap: "small",
          alignItems: "center",
          fontSize: "medium",
          _disabled: {
            cursor: "not-allowed",
          },
        }),
        props.className,
      )}
      data-pending={isPending ? "true" : undefined}
    >
      {isPending && (
        <ProgressBar
          aria-label={t(($) => $.common["Saving..."])}
          isIndeterminate
          className={css({
            zIndex: 1,
            position: "absolute",
            top: "2px",
            left: "2px",
            transform: "translateX(13px)",
            transition: "all 0.2s",
          })}
        >
          <ImSpinner2
            size={20}
            className={css({
              animation: "spin",
              color: "grey",
            })}
          />
        </ProgressBar>
      )}
      <div
        className={cx(
          "indicator",
          css({
            boxSizing: "border-box",
            border: "2px solid {colors.lightgrey}",
            borderRadius: "full",
            width: "52px",
            height: "26px",
            backgroundColor: "lightgrey",
            transition: "all 0.2s",
            _before: {
              display: "block",
              borderRadius: "16px",
              width: "22px",
              height: "22px",

              background: "white",
              transition: "all 0.2s",
              content: "''",
              "[data-pending] &": {
                transform: "translateX(13px)",
              },
              "[data-selected] &": {
                transform: "translateX(26px)",
              },
            },
            "[data-selected] &": {
              border: "2px solid {colors.trellisPalette.13}",
              backgroundColor: "trellisPalette.13",
            },
            "[data-focus-visible] &": {
              outline: "2px solid {colors.bootstrapBlue}",
              outlineOffset: "2px",
            },
            "[data-disabled] &": {
              opacity: 0.5,
            },
          }),
        )}
      ></div>
      {children}
    </BaseSwitch>
  );
}
